import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, get } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { DetailedChangeset } from 'ember-changeset/types';
import { timeout, restartableTask, dropTask } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { or } from 'ember-truth-helpers';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiInputField from 'garaje/components/ui-input-field';
import doubleConfirmation from 'garaje/helpers/double-confirmation';
import type Location from 'garaje/models/location';
import type User from 'garaje/models/user';
import type StatsigService from 'garaje/services/statsig';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import zft from 'garaje/utils/zero-for-tests';
import { MIN_CAPACITY_LIMIT, MAX_CAPACITY_LIMIT } from 'garaje/validations/location';

interface LocationSettingsCapacityLimitModalSignature {
  Args: {
    /**
     * the changeset for location settings
     */
    changeset: DetailedChangeset<Location>;
    /**
     * method to register the changeset on the controller for dirty transition checking
     */
    registerChangeset: (changeset: DetailedChangeset<Location>) => void;
    /**
     * searches users by term
     */
    onSearchUsers: (term: string) => Promise<User[]>;
    onSubmit: (changeset: DetailedChangeset<Location>) => Promise<void>;
    onConfirmCapacity: (capacityLimit: number) => Promise<boolean>;
    location: Location;
    disabled?: boolean;
  };
  Element: HTMLDivElement;
}

export default class LocationSettingsCapacityLimitModal extends Component<LocationSettingsCapacityLimitModalSignature> {
  minCapacityLimit = MIN_CAPACITY_LIMIT;
  maxCapacityLimit = MAX_CAPACITY_LIMIT;

  elementId = guidFor(this);

  @service declare workplaceMetrics: WorkplaceMetricsService;
  @service declare statsig: StatsigService;

  @tracked isSettingsPanelOpen = false;
  @tracked changeset;

  constructor(owner: unknown, args: LocationSettingsCapacityLimitModalSignature['Args']) {
    super(owner, args);
    this.changeset = this.args.changeset;
    this.args.registerChangeset(this.args.changeset);
  }

  /**
   * Ensures that PowerSelectMultiple never holds a reference to the capacityContacts hasMany relationship
   *
   * @returns List of selected capacityContacts user models in changeset
   */
  get selectedCapacityContacts(): User[] {
    return 'content' in this.args.changeset.capacityContacts
      ? this.args.changeset.capacityContacts.content
      : this.args.changeset.capacityContacts;
  }

  get isDirty(): boolean {
    const { location: currentLocation, changeset } = this.args;
    const errors = changeset.errors;

    // eslint-disable-next-line ember/no-get
    if (errors.length > 0 || get(currentLocation, 'company.hasDirtyAttributes') || changeset.isDirty) {
      return true;
    }

    return false;
  }

  /**
   * Sets the setCapacityLimit value in the location changeset
   *
   * @param capacityLimit Capacity limit input value
   */
  @action
  setCapacityLimit(capacityLimit: string): void {
    this.changeset.capacityLimit = parseInt(capacityLimit) || null;
  }

  /**
   * Sets the capacityContacts value in the location changeset
   *
   * @param capacityContacts List of user models to save as capacity contacts
   */
  @action
  setCapacityContacts(capacityContacts: User[]): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    this.args.changeset.capacityContacts = capacityContacts;
  }

  /**
   * Constrains the capacityLimit input to MAX and MIN values
   *
   * @param event input event
   * @param event.target capacity limit input element
   */
  @action
  constrainCapacity({ target }: Event): void {
    let { valueAsNumber } = <HTMLInputElement>target;
    valueAsNumber = Math.min(valueAsNumber, MAX_CAPACITY_LIMIT);
    valueAsNumber = Math.max(valueAsNumber, MIN_CAPACITY_LIMIT);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (<HTMLInputElement>target).value = valueAsNumber;
  }

  /**
   * Shows settings panel
   */
  @action
  showSettings(): void {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_CAPACITY_SETTINGS_EDIT_BUTTON_CLICKED');
    this.isSettingsPanelOpen = true;
  }

  /**
   * Hides settings panel
   */
  @action
  hideSettings(): void {
    this.isSettingsPanelOpen = false;
  }

  /**
   * Rolls back the changeset
   */
  @action
  rollback(): void {
    this.args.changeset.rollback();
  }

  /**
   * Task for searching users
   *
   * @param term Search term
   * @returns Promise for an array of search results
   */

  searchUsersTask = restartableTask(async (term: string) => {
    await timeout(zft(250));
    const capacityContacts = this.args.changeset.capacityContacts.content;
    const users = await this.args.onSearchUsers(term);
    return users.filter((user) => !capacityContacts.includes(user));
  });

  /**
   * Task for saving location model changes
   *
   * @param event DOM event
   */
  saveTask = dropTask(async (event: Event) => {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_CAPACITY_SETTINGS_SAVE_BUTTON_CLICKED');
    event.preventDefault();
    const { changeset } = this.args;
    const { onSubmit, onConfirmCapacity } = this.args;
    const capacityLimit = <number>changeset.change['capacityLimit'];
    await changeset.validate();

    let shouldSaveChanges = <boolean>changeset.isValid;
    if (isPresent(capacityLimit)) {
      shouldSaveChanges = await onConfirmCapacity(capacityLimit);
    }
    if (shouldSaveChanges) {
      await onSubmit(changeset);
    }
  });

  /**
   * Task for disabling capacityLimit and saving location model
   *
   * @param event DOM event
   */
  disableTask = dropTask(async (event: Event) => {
    const { changeset } = this.args;
    this.changeset.capacityLimit = null;
    await changeset.validate();
    changeset.rollbackInvalid();
    await this.saveTask.perform(event);
    this.hideSettings();
  });

  <template>
    <form {{on "submit" this.saveTask.perform}}>
      <SettingsPanel
        @isExpandable={{true}}
        @isOpen={{this.isSettingsPanelOpen}}
        @onCancel={{doubleConfirmation close=this.hideSettings rollback=this.rollback dirty=this.isDirty}}
        ...attributes
        as |sp|
      >
        {{#let sp.header sp.body sp.footer as |PanelHeader PanelBody PanelFooter|}}
          <PanelHeader
            @iconUri="/assets/images/features/visitor.svg"
            @title="Daily capacity limit"
            @description="Set a limit for the number of people who can be signed in or expected at any one time."
          >
            {{#if this.isSettingsPanelOpen}}
              <button
                type="submit"
                class="btn btn--small btn--brand"
                data-test-capacity-save-button
                disabled={{or this.saveTask.isRunning @changeset.isInvalid @changeset.isPristine}}
              >
                {{if this.saveTask.isRunning "Saving..." "Save"}}
              </button>
            {{else}}
              <button
                type="button"
                {{on "click" this.showSettings}}
                disabled={{@disabled}}
                class="btn btn--small btn--brand"
                data-test-capacity-open-button
              >
                {{if @location.capacityLimitEnabled "Edit" "Enable"}}
              </button>
            {{/if}}
          </PanelHeader>
          <PanelBody as |PanelContent|>
            <PanelContent class="mr-8">
              <MessageBox @type="info" class="my-8" data-test-capacity-message-box>
                <h5 class="mb-2">Daily capacity is based on a 24-hour period from 12:00 am - 11:59 pm, local time.</h5>
                <ul class="pl-4 m-0">
                  <li class="mb-1">Occupancy increases when visitors are invited or sign in, or when employees register
                    or sign in.</li>
                  <li>Occupancy decreases when employees and visitors sign out, or when you delete invites or entries.</li>
                </ul>
              </MessageBox>
              <div class="flex">
                <UiInputField class="w-1/3" @required={{true}} as |Field|>
                  <Field.label data-test-capacity-limit-label>Set daily capacity</Field.label>
                  <Field.input
                    data-test-capacity-limit-input
                    {{on "input" this.constrainCapacity}}
                    @value={{@changeset.capacityLimit}}
                    @update={{this.setCapacityLimit}}
                    min={{this.minCapacityLimit}}
                    max={{this.maxCapacityLimit}}
                    type="number"
                  />
                </UiInputField>
              </div>

              <hr class="my-8" />

              <h4>Capacity notifications</h4>
              <p>Choose admins to receive capacity related emails.</p>
              <label
                data-test-capacity-email-label
                id={{concat "capacity-email-label-" this.elementId}}
                for={{concat "capacity-email-select-" this.elementId}}
              >Choose recipient(s)</label>
              <PowerSelectMultiple
                @triggerId={{concat "capacity-email-select-" this.elementId}}
                @ariaLabelledBy={{concat "capacity-email-label-" this.elementId}}
                @allowClear={{true}}
                @searchEnabled={{true}}
                @selected={{this.selectedCapacityContacts}}
                @search={{this.searchUsersTask.perform}}
                @searchField="fullName"
                @onChange={{this.setCapacityContacts}}
                @placeholder="Choose a contact"
                as |user|
              >
                {{user.fullName}}
              </PowerSelectMultiple>
            </PanelContent>
            {{#if (or this.disableTask.isRunning @location.capacityLimitEnabled)}}
              <PanelFooter>
                <button
                  data-test-capacity-disable-button
                  type="button"
                  class="btn btn--small btn--outlineFill"
                  disabled={{this.saveTask.isRunning}}
                  {{on "click" this.disableTask.perform}}
                >
                  {{if this.disableTask.isRunning "Disabling..." "Disable"}}
                </button>
              </PanelFooter>
            {{/if}}
          </PanelBody>
        {{/let}}
      </SettingsPanel>
    </form>
  </template>
}
